import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { navItems } from "../data/content";
import { site } from "../theme";

// ─── Hava Durumu ─────────────────────────────────────────────────────────────
const OWM_KEY = "04157835e1405586d8f15a25e325b781";

interface WeatherData {
  temp: number;
  desc: string;
  icon: string;
}

// OWM icon kodu → Font Awesome ikonu + renk
function owmToFa(icon: string): { fa: string; color: string } {
  const code = icon.replace("d", "").replace("n", "");
  if (code === "01") return { fa: "fa-sun", color: "#FCD34D" };              // açık
  if (code === "02") return { fa: "fa-cloud-sun", color: "#FCD34D" };        // az bulutlu
  if (code === "03") return { fa: "fa-cloud", color: "#CBD5E1" };            // bulutlu
  if (code === "04") return { fa: "fa-cloud-meatball", color: "#94A3B8" };   // çok bulutlu
  if (code === "09") return { fa: "fa-cloud-showers-heavy", color: "#7DD3FC" }; // sağanak
  if (code === "10") return { fa: "fa-cloud-rain", color: "#60A5FA" };       // yağmurlu
  if (code === "11") return { fa: "fa-cloud-bolt", color: "#A78BFA" };       // fırtına
  if (code === "13") return { fa: "fa-snowflake", color: "#BAE6FD" };        // karlı
  if (code === "50") return { fa: "fa-wind", color: "#CBD5E1" };             // sisli/dumanlı
  return { fa: "fa-cloud", color: "#CBD5E1" };
}

const WeatherWidget: React.FC<{ city: string; label: string }> = ({ city, label }) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);

  useEffect(() => {
    fetch(`https://api.openweathermap.org/data/2.5/weather?q=${city},TR&appid=${OWM_KEY}&units=metric&lang=tr`)
      .then((r) => r.json())
      .then((d) => {
        if (d.main) {
          setWeather({
            temp: Math.round(d.main.temp),
            desc: d.weather[0].description,
            icon: d.weather[0].icon,
          });
        }
      })
      .catch(() => {});
  }, [city]);

  if (!weather) return null;

  const { fa, color } = owmToFa(weather.icon);

  return (
    <div className="flex items-center gap-2 text-white/90 hover:text-white transition-colors" title={weather.desc}>
      <i className={`fa-solid ${fa} text-xl drop-shadow`} style={{ color }} />
      <div className="flex flex-col leading-none gap-0.5">
        <span className="font-bold text-white text-base">{weather.temp}°C</span>
        <span className="text-white/70 text-xs font-medium">{label}</span>
      </div>
    </div>
  );
};

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = (href: string) => {
    setActiveDropdown((prev) => (prev === href ? null : href));
  };

  return (
    <header ref={navRef} className="fixed top-0 left-0 right-0 z-50">

      {/* ── Katman 1: Top Bar ── */}
      <div className="bg-brand-blue hidden md:block">
        <div className="w-full px-6 xl:px-12 flex justify-between items-center h-11 text-xs text-white/80">
          {/* Sol: iletişim */}
          <div className="flex items-center gap-5">
            <a href={`tel:${site.phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
              <i className="fa-solid fa-phone text-[11px]" />
              {site.phone}
            </a>
            <a href={`mailto:${site.email}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
              <i className="fa-solid fa-envelope text-[11px]" />
              {site.email}
            </a>
          </div>
          {/* Orta: hava durumu */}
          <div className="flex items-center gap-4">
            <WeatherWidget city="Trabzon" label="Trabzon" />
            <div className="w-px h-3 bg-white/20" />
            <WeatherWidget city="Bursa" label="Bursa" />
          </div>
          {/* Sağ: sosyal medya */}
          <div className="flex items-center gap-2">
            <a href="https://www.instagram.com/bursatrabzonlulardernegi/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-6 h-6 flex items-center justify-center rounded border border-white/20 hover:bg-white/20 hover:text-white transition-colors">
              <i className="fa-brands fa-instagram text-[11px]" />
            </a>
            <a href="/" aria-label="WhatsApp" className="w-6 h-6 flex items-center justify-center rounded border border-white/20 hover:bg-white/20 hover:text-white transition-colors">
              <i className="fa-brands fa-whatsapp text-[11px]" />
            </a>
          </div>
        </div>
      </div>

      {/* ── Katman 2: Logo Bar ── */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="w-full px-6 xl:px-12 flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 h-full py-1 min-w-0">
            <img src="/Trabzonlular_transparency.png" alt="Bursa Trabzonlular Derneği" className="h-full w-auto object-contain drop-shadow-md flex-shrink-0" />
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-[#0F172A] font-bold text-sm sm:text-base md:text-xl lg:text-[1.35rem] leading-snug tracking-normal" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                Bursa Trabzon Kültür ve Yardımlaşma Derneği
              </span>
            </div>
          </Link>

          {/* Sağ: arama + üye ol */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0 ml-4">
            {searchOpen ? (
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  type="text"
                  placeholder="Sitede ara..."
                  className="border border-gray-200 rounded-md px-3 py-1.5 text-sm w-52 focus:outline-none focus:border-brand-blue"
                />
                <button onClick={() => setSearchOpen(false)} className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center">
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="text-gray-400 hover:text-brand-blue transition-colors w-8 h-8 flex items-center justify-center"
                aria-label="Ara"
              >
                <i className="fa-solid fa-magnifying-glass text-base" />
              </button>
            )}
            <Link
              to="/destek/uyelik"
              className="bg-brand-bordo text-white text-sm font-semibold px-5 py-2 rounded-md hover:bg-brand-bordo-dark transition-colors flex items-center gap-2"
            >
              <i className="fa-solid fa-user-plus text-xs" />
              Üyelik Başvurusu
            </Link>
          </div>

          {/* Mobil: hamburger */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-brand-blue p-2 text-xl flex-shrink-0 ml-2"
          >
            <i className={isOpen ? "fa-solid fa-xmark" : "fa-solid fa-bars"} />
          </button>
        </div>
      </div>

      {/* ── Katman 3: Ana Menü Bar ── */}
      <div
        className={`hidden lg:block transition-all duration-300 ${scrolled ? "shadow-md" : ""}`}
        style={{ backgroundColor: "#0F172A" }}
      >
        <div className="w-full px-6 xl:px-12">
          <div className="flex items-stretch h-14">
            {navItems.map((item) => {
              const isActive =
                location.pathname === item.href ||
                location.pathname.startsWith(item.href + "/");
              return (
                <div
                  key={item.href}
                  className="relative"
                  onMouseEnter={() => item.children && setActiveDropdown(item.href)}
                  onMouseLeave={() => item.children && setActiveDropdown(null)}
                >
                  {item.children ? (
                    <button
                      onClick={() => toggleDropdown(item.href)}
                      className={`flex items-center gap-1.5 px-5 h-full text-[15px] font-medium transition-colors duration-150 border-b-[3px] ${
                        isActive
                          ? "text-white border-brand-bordo"
                          : "text-white/90 border-transparent hover:text-white hover:border-brand-bordo"
                      }`}
                    >
                      {item.label}
                      <i className={`fa-solid fa-chevron-down text-[11px] transition-transform duration-200 ${activeDropdown === item.href ? "rotate-180" : ""}`} />
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className={`flex items-center px-5 h-full text-[15px] font-medium transition-colors duration-150 border-b-[3px] ${
                        isActive
                          ? "text-white border-brand-bordo"
                          : "text-white/90 border-transparent hover:text-white hover:border-brand-bordo"
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}

                  {/* Dropdown */}
                  {item.children && activeDropdown === item.href && (
                    <div className="absolute top-full left-0 w-64 bg-white border border-gray-100 shadow-xl rounded-b-lg overflow-hidden z-50">
                      {item.children.map((child, idx) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`flex items-center gap-2.5 px-5 py-3 text-[14px] text-gray-700 hover:bg-brand-blue hover:text-white transition-colors ${
                            idx !== 0 ? "border-t border-gray-50" : ""
                          }`}
                        >
                          <i className="fa-solid fa-angle-right text-[10px] text-gray-400 group-hover:text-white" />
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Mobil Menü ── */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl max-h-[80vh] overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.href}>
              <Link
                to={item.href}
                className="flex items-center gap-3 px-5 py-3.5 text-gray-800 hover:text-brand-blue hover:bg-gray-50 font-medium border-b border-gray-100 text-sm"
              >
                <i className="fa-solid fa-angle-right text-xs text-gray-400" />
                {item.label}
              </Link>
              {item.children && (
                <div className="bg-gray-50">
                  {item.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className="flex items-center gap-2 pl-9 pr-5 py-2.5 text-sm text-gray-600 hover:text-brand-blue hover:bg-gray-100 border-b border-gray-100"
                    >
                      <i className="fa-solid fa-minus text-[9px] text-gray-400" />
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="p-4 bg-gray-50">
            <Link
              to="/destek/uyelik"
              className="flex items-center justify-center gap-2 w-full bg-brand-bordo text-white font-semibold py-3 rounded-md hover:bg-brand-bordo-dark transition-colors"
            >
              <i className="fa-solid fa-user-plus text-sm" />
              Üyelik Başvurusu
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
